import { useState, useEffect, useCallback } from 'react';
import { useNavigateWithUtm } from '../hooks/useNavigateWithUtm';
import { 
  AtSign, Shield, Search, User, Lock, Eye, AlertTriangle, 
  Heart, MessageCircle, Check, ArrowLeft, Wifi, Users, 
  Loader2
} from 'lucide-react';

// ==================== CONFIGURAÇÃO ====================
// Usar a mesma API do backend que a Home.jsx usa (funciona via proxy)
const API_URL = '/api/instagram/profile';

// ==================== CSS GLOBAL INTERNO ====================
const GlobalCSS = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .animate-spin {
      animation: spin 1s linear infinite;
    }
    
    .spy-container {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .spy-input:focus {
      border-color: #ec4899 !important;
      outline: none !important;
    }
  `}</style>
);

// ==================== TIPOS ====================
interface InstagramProfile {
  identity: {
    username: string;
    full_name: string;
    id: string;
    biography: string;
    profile_pic_url_hd: string;
    external_url: string;
    bio_links: Array<{
      title: string;
      url: string;
      link_type: string;
    }>;
  };
  metrics: {
    followers: number;
    following: number;
    posts: number;
  };
  account_type: {
    is_verified: boolean;
    is_private: boolean;
    is_business_account: boolean;
    is_professional_account: boolean;
  };
  business_info: {
    category_name?: string;
    email?: string;
    phone_number?: string;
  };
  content: {
    has_clips: boolean;
    highlight_reel_count: number;
  };
  recent_posts: InstagramPost[];
}

interface InstagramPost {
  id: string;
  shortcode: string;
  display_url: string;
  is_video: boolean;
  likes: number;
  comments: number;
  timestamp: number;
  caption: string;
}

interface SearchStep {
  id: number;
  text: string;
  status: 'pending' | 'loading' | 'completed';
}

const initialSteps: SearchStep[] = [
  { id: 1, text: 'Conectando aos servidores do Instagram...', status: 'pending' },
  { id: 2, text: 'Procurando perfil do usuário...', status: 'pending' },
  { id: 3, text: 'Buscando dados do perfil anonimamente...', status: 'pending' },
  { id: 4, text: 'Analisando seguidores recentes...', status: 'pending' },
  { id: 5, text: 'Finalizando e protegendo dados...', status: 'pending' },
];

// ==================== COMPONENTE DE AVATAR ====================
function Avatar({ src, alt, style }: { src?: string; alt: string; style?: React.CSSProperties }) {
  const [error, setError] = useState(false);
  const proxiedSrc = src ? `https://images.weserv.nl/?url=${encodeURIComponent(src)}` : null;
  
  if (error || !proxiedSrc) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}>
        <User style={{ width: '50%', height: '50%', color: 'white' }} />
      </div>
    );
  }

  return (
    <img
      src={proxiedSrc}
      alt={alt}
      style={{ objectFit: 'cover', ...style }}
      onError={() => setError(true)}
    />
  );
}

// ==================== HOOK DO CONTADOR ====================
function useSpyCounter(startFrom: number = 103847) {
  const [count, setCount] = useState(startFrom);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 5) + 1);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return count;
}

// ==================== FUNÇÕES UTILITÁRIAS ====================
function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// ==================== COMPONENTE PRINCIPAL ====================
export function InstagramSpyOffer() {
  const navigate = useNavigateWithUtm();
  const [username, setUsername] = useState('');
  const [currentScreen, setCurrentScreen] = useState<'search' | 'confirm' | 'analyzing'>('search');
  const [progress, setProgress] = useState(0);
  const [searchProgress, setSearchProgress] = useState(0);
  const [steps, setSteps] = useState<SearchStep[]>(initialSteps);
  const [profile, setProfile] = useState<InstagramProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const spyCount = useSpyCounter();

  const updateStep = useCallback((stepId: number, status: SearchStep['status']) => {
    setSteps(prev => prev.map(step => step.id === stepId ? { ...step, status } : step));
  }, []);

  const simulateProgress = useCallback(async () => {
    updateStep(1, 'loading');
    await new Promise(r => setTimeout(r, 800));
    updateStep(1, 'completed');
    setProgress(20);

    updateStep(2, 'loading');
    await new Promise(r => setTimeout(r, 1000));
    updateStep(2, 'completed');
    setProgress(40);

    updateStep(3, 'loading');
    await new Promise(r => setTimeout(r, 1200));
    updateStep(3, 'completed');
    setProgress(60);

    updateStep(4, 'loading');
    await new Promise(r => setTimeout(r, 1500));
    updateStep(4, 'completed');
    setProgress(80);

    updateStep(5, 'loading');
    await new Promise(r => setTimeout(r, 1000));
    updateStep(5, 'completed');
    setProgress(100);
  }, [updateStep]);

  const searchProfile = async (searchUsername: string) => {
    setUsername(searchUsername);
    setError(null);
    setIsSearching(true);
    setSearchProgress(0);
    
    const interval = setInterval(() => {
      setSearchProgress(prev => {
        if (prev >= 90) return 90;
        return prev + 10;
      });
    }, 150);
    
    try {
      const response = await fetch(
        `${API_URL}?username=${encodeURIComponent(searchUsername)}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Perfil não encontrado');
      }

      const data = await response.json();
      
      // Verifica se a API retornou sucesso
      if (!data.success) {
        throw new Error(data.message || 'Erro ao buscar perfil');
      }
      
      // Adaptar formato da API do backend para o formato do componente
      const apiProfile = data.profile;
      const adaptedProfile: InstagramProfile = {
        identity: {
          username: searchUsername,
          full_name: apiProfile.name || searchUsername,
          id: '',
          biography: apiProfile.bio || '',
          profile_pic_url_hd: apiProfile.avatar || '',
          external_url: apiProfile.external_link || '',
          bio_links: []
        },
        metrics: {
          followers: apiProfile.followers || 0,
          following: apiProfile.following || 0,
          posts: apiProfile.posts || 0
        },
        account_type: {
          is_verified: apiProfile.is_verified || false,
          is_private: false,
          is_business_account: false,
          is_professional_account: false
        },
        business_info: {},
        content: {
          has_clips: false,
          highlight_reel_count: 0
        },
        recent_posts: []
      };
      
      setProfile(adaptedProfile);
      
      clearInterval(interval);
      setSearchProgress(100);
      setTimeout(() => {
        setCurrentScreen('confirm');
        setIsSearching(false);
      }, 300);
    } catch (err) {
      clearInterval(interval);
      setError(err instanceof Error ? err.message : 'Erro ao buscar perfil');
      setIsSearching(false);
    }
  };

  const confirmProfile = () => {
    setCurrentScreen('analyzing');
    simulateProgress();
  };

  const editSearch = () => {
    setCurrentScreen('search');
    setProfile(null);
    setUsername('');
    setSearchProgress(0);
  };

  const reset = () => {
    setUsername('');
    setCurrentScreen('search');
    setProgress(0);
    setSearchProgress(0);
    setSteps(initialSteps);
    setProfile(null);
    setError(null);
  };

  const handleAccessAccount = async () => {
    if (!profile) return;
    
    // Salvar dados no localStorage igual ao fluxo do Home
    localStorage.setItem('current_username', username);
    
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    const trialExpires = now + fiveMinutes;
    
    localStorage.setItem('trial_start', now.toString());
    localStorage.setItem('trial_expires', trialExpires.toString());
    localStorage.setItem('trial_active', 'true');
    
    // Converter imagem do Instagram para base64 (evita CORS)
    const imageUrl = profile.identity.profile_pic_url_hd || profile.identity.profile_pic_url || '';
    let base64Image = '';
    
    if (imageUrl && (imageUrl.includes('instagram') || imageUrl.includes('fbcdn'))) {
      try {
        const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
        const response = await fetch(proxyUrl);
        const blob = await response.blob();
        base64Image = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.warn('[Busca] Erro ao converter imagem:', e);
        base64Image = `https://i.pravatar.cc/150?u=${username}`;
      }
    } else {
      base64Image = imageUrl || `https://i.pravatar.cc/150?u=${username}`;
    }
    
    // Criar objeto de perfil compatível com o formato do Feed.jsx
    const profileData = {
      profileImageUrl: base64Image,
      fullName: profile.identity.full_name || username,
      bio: profile.identity.biography || '',
      postCount: profile.metrics.posts || 0,
      followersCount: profile.metrics.followers || 0,
      followingCount: profile.metrics.following || 0,
      is_verified: profile.account_type.is_verified || false,
      is_private: profile.account_type.is_private || false,
      fromApi: true
    };
    
    localStorage.setItem('current_profile', JSON.stringify(profileData));
    
    // Navegar para o feed
    navigate('/feed');
  };

  // RENDER DAS TELAS
  return (
    <div className="spy-container">
      <GlobalCSS />
      
      {currentScreen === 'search' && (
        <div style={{ 
          minHeight: '100vh',
          background: 'linear-gradient(to bottom, #fdf2f8 0%, #ffffff 50%, #faf5ff 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px 16px'
        }}>
          <div style={{ maxWidth: '380px', width: '100%', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #ec4899 0%, #9333ea 100%)',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 10px 15px -3px rgba(236, 72, 153, 0.3)'
              }}>
                <Eye style={{ width: '40px', height: '40px', color: 'white' }} />
              </div>
              
              <h1 style={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '12px',
                lineHeight: 1.25
              }}>
                Descubra a <span style={{
                  background: 'linear-gradient(135deg, #ec4899 0%, #9333ea 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>verdade</span>
              </h1>
              
              <p style={{ color: '#4b5563', fontSize: '16px', lineHeight: 1.625 }}>
                Acesse atividades ocultas de qualquer perfil do Instagram. <strong style={{ color: '#db2777' }}>100% anônimo.</strong>
              </p>
            </div>

            {/* Search Form */}
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                if (username.trim()) searchProfile(username.trim());
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}
            >
              <div style={{ position: 'relative' }}>
                <AtSign style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  width: '20px', 
                  height: '20px', 
                  color: '#9ca3af' 
                }} />
                <input
                  type="text"
                  placeholder="Digite o @usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isSearching}
                  className="spy-input"
                  style={{
                    width: '100%',
                    paddingLeft: '48px',
                    paddingRight: '16px',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    outline: 'none',
                    opacity: isSearching ? 0.5 : 1,
                    cursor: isSearching ? 'not-allowed' : 'text',
                    boxSizing: 'border-box',
                    height: '56px'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={!username.trim() || isSearching}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: 'linear-gradient(to right, #ec4899, #9333ea)',
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: 'none',
                  cursor: !username.trim() || isSearching ? 'not-allowed' : 'pointer',
                  opacity: !username.trim() || isSearching ? 0.7 : 1,
                  boxShadow: '0 10px 15px -3px rgba(236, 72, 153, 0.3)',
                  fontSize: '16px',
                  height: '56px',
                  boxSizing: 'border-box'
                }}
              >
                <Search style={{ width: '20px', height: '20px' }} />
                INICIAR ESPIONAGEM
              </button>

              {/* Barra de Progresso */}
              {isSearching && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#6b7280' }}>
                    <span>Buscando perfil...</span>
                    <span>{searchProgress}%</span>
                  </div>
                  <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        background: 'linear-gradient(to right, #ec4899, #9333ea)',
                        borderRadius: '9999px',
                        width: `${searchProgress}%`,
                        transition: 'width 0.2s'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Contador */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: '#d97706',
                backgroundColor: '#fffbeb',
                borderRadius: '8px',
                padding: '10px 12px'
              }}>
                <AlertTriangle style={{ width: '16px', height: '16px' }} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>
                  🔥 {formatNumber(spyCount)} pessoas espionando agora
                </span>
              </div>
            </form>

            {/* Features */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
              {[
                { icon: Heart, color: '#ec4899', bg: '#fce7f3', text: 'Veja quem ele/ela seguiu recentemente' },
                { icon: MessageCircle, color: '#9333ea', bg: '#f3e8ff', text: 'Leia mensagens privadas (DMs)' },
                { icon: Eye, color: '#4f46e5', bg: '#e0e7ff', text: 'Histórico de curtidas e comentários' }
              ].map((feature, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '14px',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f3f4f6'
                }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    backgroundColor: feature.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <feature.icon style={{ width: '22px', height: '22px', color: feature.color }} />
                  </div>
                  <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500, lineHeight: 1.4 }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '32px' }}>
              {[
                { icon: Shield, text: 'SSL Seguro' },
                { icon: User, text: 'Anônimo' },
                { icon: Lock, text: 'Sem rastros' }
              ].map((badge, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280' }}>
                  <badge.icon style={{ width: '14px', height: '14px', color: '#22c55e' }} />
                  {badge.text}
                </div>
              ))}
            </div>

            {/* Social Proof */}
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '12px' }}>Últimas pessoas que descobriram a verdade:</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <img
                    key={i}
                    src={`https://i.pravatar.cc/150?img=${20 + i}`}
                    alt=""
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: '2px solid white',
                      marginLeft: i > 1 ? '-10px' : 0
                    }}
                  />
                ))}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#f3f4f6',
                  border: '2px solid white',
                  marginLeft: '-10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: '#4b5563',
                  fontWeight: 700
                }}>
                  +5K
                </div>
              </div>
            </div>
          </div>

          {/* Error Modal */}
          {error && (
            <div style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              padding: '16px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '24px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '32px',
                maxWidth: '360px',
                width: '100%',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <AlertTriangle style={{ width: '32px', height: '32px', color: '#ef4444' }} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Erro na busca</h3>
                <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '15px' }}>{error}</p>
                <button
                  onClick={reset}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(to right, #ec4899, #9333ea)',
                    color: 'white',
                    fontWeight: 700,
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {currentScreen === 'confirm' && profile && (
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(to bottom, #fdf2f8 0%, #ffffff 50%, #faf5ff 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '32px 16px'
        }}>
          <div style={{ maxWidth: '380px', width: '100%', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Perfil encontrado!</h1>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>Confirme se é esta a pessoa</p>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '28px',
              boxShadow: '0 20px 25px -5px rgba(236, 72, 153, 0.1)',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: '#f472b6',
                    borderRadius: '50%',
                    filter: 'blur(24px)',
                    opacity: 0.2,
                    transform: 'scale(1.1)'
                  }} />
                  <div style={{
                    position: 'relative',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
                    padding: '4px'
                  }}>
                    <Avatar
                      src={profile.identity.profile_pic_url_hd}
                      alt={profile.identity.username}
                      style={{ width: '100%', height: '100%', borderRadius: '50%', border: '4px solid white' }}
                    />
                  </div>
                  {profile.account_type.is_verified && (
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      right: '4px',
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#3b82f6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid white'
                    }}>
                      <svg style={{ width: '18px', height: '18px', color: 'white' }} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>{profile.identity.full_name}</h2>
                <p style={{ color: '#6b7280', fontSize: '16px' }}>@{profile.identity.username}</p>
              </div>

              {profile.identity.biography && (
                <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                  <p style={{ textAlign: 'center', color: '#4b5563', fontSize: '15px', lineHeight: 1.5 }}>{profile.identity.biography}</p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, color: '#111827', fontSize: '18px' }}>{profile.metrics.posts}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Posts</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, color: '#111827', fontSize: '18px' }}>{(profile.metrics.followers / 1000000).toFixed(1)}M</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Seguidores</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, color: '#111827', fontSize: '18px' }}>{profile.metrics.following}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Seguindo</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={confirmProfile}
                style={{
                  width: '100%',
                  padding: '18px',
                  background: 'linear-gradient(to right, #ec4899, #9333ea)',
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 10px 15px -3px rgba(236, 72, 153, 0.3)',
                  fontSize: '16px'
                }}
              >
                <Check style={{ width: '22px', height: '22px' }} />
                SIM, É ESTA PESSOA
              </button>

              <button
                onClick={editSearch}
                style={{
                  width: '100%',
                  padding: '18px',
                  backgroundColor: 'white',
                  border: '2px solid #e5e7eb',
                  color: '#374151',
                  fontWeight: 600,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                <ArrowLeft style={{ width: '22px', height: '22px' }} />
                NÃO, CORRIGIR BUSCA
              </button>
            </div>
          </div>
        </div>
      )}

      {currentScreen === 'analyzing' && profile && (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#f9fafb',
          display: 'flex',
          flexDirection: 'column',
          padding: '32px 16px'
        }}>
          <div style={{ maxWidth: '380px', width: '100%', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>
                {progress === 100 ? 'Análise completa!' : 'Analisando seguidores'}
              </h1>
              <p style={{ color: '#6b7280', fontSize: '16px' }}>@{username}</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: '#f472b6',
                  borderRadius: '50%',
                  filter: 'blur(24px)',
                  opacity: 0.3,
                  transform: 'scale(1.1)'
                }} />
                <div style={{
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)',
                  padding: '4px'
                }}>
                  <Avatar
                    src={profile.identity.profile_pic_url_hd}
                    alt={profile.identity.username}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', border: '4px solid white' }}
                  />
                </div>
              </div>
              
              {profile.identity.biography && (
                <p style={{ textAlign: 'center', color: '#4b5563', fontSize: '15px', maxWidth: '320px', marginBottom: '20px', lineHeight: 1.5 }}>{profile.identity.biography}</p>
              )}

              {progress === 100 && (
                <div style={{ width: '100%' }}>
                  <button
                    onClick={handleAccessAccount}
                    style={{
                      width: '100%',
                      padding: '18px',
                      background: 'linear-gradient(to right, #22c55e, #059669)',
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 10px 15px -3px rgba(34, 197, 94, 0.3)',
                      fontSize: '16px'
                    }}
                  >
                    ACESSAR CONTA
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '13px', color: '#6b7280', marginTop: '10px' }}>
                    🔒 Clique para acessar a conta completa
                  </p>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>Progresso</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{progress}%</span>
              </div>
              <div style={{ height: '10px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    background: 'linear-gradient(to right, #f472b6, #ec4899)',
                    borderRadius: '9999px',
                    width: `${progress}%`,
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {steps.map((step) => {
                const stepIcons: Record<number, React.ElementType> = {
                  1: Wifi, 2: Search, 3: Eye, 4: Users, 5: Shield,
                };
                const Icon = stepIcons[step.id];
                const isCompleted = step.status === 'completed';
                const isLoading = step.status === 'loading';

                return (
                  <div
                    key={step.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      borderRadius: '12px',
                      backgroundColor: isCompleted ? '#f0fdf4' : isLoading ? '#eff6ff' : 'white',
                      border: `2px solid ${isCompleted ? '#bbf7d0' : isLoading ? '#bfdbfe' : '#e5e7eb'}`
                    }}
                  >
                    <div
                      style={{
                        flexShrink: 0,
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isCompleted ? '#dcfce7' : isLoading ? '#dbeafe' : '#f3f4f6'
                      }}
                    >
                      {isCompleted ? (
                        <Check style={{ width: '22px', height: '22px', color: '#16a34a' }} />
                      ) : isLoading ? (
                        <Loader2 style={{ width: '22px', height: '22px', color: '#3b82f6' }} className="animate-spin" />
                      ) : (
                        <Icon style={{ width: '22px', height: '22px', color: '#9ca3af' }} />
                      )}
                    </div>

                    <span
                      style={{
                        flex: 1,
                        fontWeight: 500,
                        fontSize: '15px',
                        color: isCompleted ? '#166534' : isLoading ? '#1e40af' : '#9ca3af'
                      }}
                    >
                      {step.text}
                    </span>

                    {isCompleted && (
                      <Check style={{ width: '22px', height: '22px', color: '#22c55e', flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InstagramSpyOffer;
